// Pure helpers for the stats cards (no DOM/React): pick a metric category out of
// the catalogue and shape it into display groups + the flat key list to query.
// One shared module drives all the stat widgets (Passing / Attacking / Defending
// / Goalkeeping) — they differ only by which category they pick.
// Value formatting is reused from v1's fullStats.ts (a keeper).

import type { MetricCategory } from "../data/stats.js";

export interface StatRow {
  key: string;
  label: string;
  outputType?: string | null;
  definition?: string;
}

export interface StatGroup {
  id: string;
  name: string;
  rows: StatRow[];
}

/** A category from the metric catalogue, matched by id or name (case-insensitive),
 *  or null if absent. `key` is the category id, e.g. "passing" / "goalkeeping". */
export function findCategory(
  cats: MetricCategory[] | undefined,
  key: string
): MetricCategory | null {
  if (!cats) return null;
  const k = key.toLowerCase();
  return cats.find((c) => c.id.toLowerCase() === k || c.name.toLowerCase() === k) ?? null;
}

/** Shape a category into display groups (subcategory → metric rows). Prefer the
 *  short label, fall back to the full name. */
export function categoryGroups(cat: MetricCategory | null): StatGroup[] {
  if (!cat) return [];
  return cat.subcategories.map((s) => ({
    id: s.id,
    name: s.name,
    rows: s.playerMetrics.map((m) => ({
      key: m.key,
      label: m.shortName || m.name,
      outputType: m.outputType,
      definition: m.definition,
    })),
  }));
}

/** Every metric key across the groups — the `keys` for the /player-stats query. */
export function categoryKeys(groups: StatGroup[]): string[] {
  return groups.flatMap((g) => g.rows.map((r) => r.key));
}

/** Does a player's stats row hold at least one real value across `keys`? "Real" =
 *  present AND non-zero: the engine returns 0 (not null) for a category's metrics
 *  when they don't apply to a player (e.g. an outfielder's keeper metrics), so a
 *  null-only test would wrongly see data. No row, or every key null/0, → false.
 *  Pure, so the "no data" gate is decided in one tested place, not in the view. */
export function rowHasData(
  row: Record<string, unknown> | undefined,
  keys: string[]
): boolean {
  if (!row) return false;
  return keys.some((k) => {
    const v = row[k];
    return v !== null && v !== undefined && v !== 0;
  });
}

/** Which keys decide whether a player belongs to a category. For most categories
 *  every metric counts (so pass all keys). Goalkeeping is special: an outfielder
 *  has the keeper-only metrics at 0 BUT some team-context ones (e.g.
 *  `opponent_np_shots`) non-zero, so the gate must look only at keeper-specific
 *  metrics. Falls back to all keys when none of the given signal keys are in the
 *  catalogue — so a metric rename can't wrongly hide a real keeper's data. */
export function effectiveSignalKeys(
  signalKeys: string[] | undefined,
  allKeys: string[]
): string[] {
  if (!signalKeys) return allKeys;
  const present = signalKeys.filter((k) => allKeys.includes(k));
  return present.length ? present : allKeys;
}
